import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

/**
 * Configuration interface for the API client
 */
interface ApiClientConfig {
  baseURL: string;
  timeout?: number;
}

/**
 * Centralized API client using Axios
 * Provides standardized HTTP communication with request/response interceptors
 * Includes automatic authentication token injection and refresh handling
 */
class ApiClient {
  private client: AxiosInstance;
  private tokenProvider?: () => Promise<string | null>;
  private idTokenProvider?: () => Promise<string | null>;

  constructor(config: ApiClientConfig) {
    this.client = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 15000, // 15 second default timeout
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Set the token provider function for authentication
   * @param provider Function that returns a promise resolving to an access token, or undefined to clear
   */
  setTokenProvider(provider: (() => Promise<string | null>) | undefined): void {
    this.tokenProvider = provider;
  }

  /**
   * Set the ID token provider function for authentication
   * @param provider Function that returns a promise resolving to an ID token, or undefined to clear
   */
  setIdTokenProvider(provider: (() => Promise<string | null>) | undefined): void {
    this.idTokenProvider = provider;
  }

  /**
   * Set up request and response interceptors
   * Handles authentication token injection and automatic token refresh
   */
  private setupInterceptors(): void {
    // Request interceptor - automatically add authentication tokens
    this.client.interceptors.request.use(
      async (config) => {
        // Log outgoing requests for debugging
        console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
        
        // Add authentication headers when token providers are available
        if (this.tokenProvider) {
          try {
            const token = await this.tokenProvider();
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
            }
          } catch (error) {
            console.error('Failed to get access token:', error);
            // Continue without auth header on error
          }
        }

        // Add ID token header for improved authentication
        if (this.idTokenProvider) {
          try {
            const idToken = await this.idTokenProvider();
            if (idToken) {
              config.headers['X-ID-Token'] = idToken;
            }
          } catch (error) {
            console.error('Failed to get ID token:', error);
            // Continue without ID token header on error
          }
        }
        
        return config;
      },
      (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor - standardize error handling and automatic token refresh
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        // Log successful responses for debugging
        console.log(`API Response: ${response.status} ${response.config.url}`);
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
        
        // Standardize error handling
        console.error('API Error:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url,
          method: error.config?.method,
        });

        // Handle authentication errors with automatic token refresh
        if (error.response?.status === 401 && !originalRequest._retry && this.tokenProvider) {
          console.log('Received 401, attempting token refresh...');
          originalRequest._retry = true;
          
          try {
            // Try to get fresh tokens
            const newToken = await this.tokenProvider();
            const newIdToken = this.idTokenProvider ? await this.idTokenProvider() : null;
            
            if (newToken) {
              // Update the authorization headers with the new tokens
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              
              if (newIdToken) {
                originalRequest.headers['X-ID-Token'] = newIdToken;
              }
              
              // Retry the original request
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            // Let the error propagate - the auth provider will handle logout
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * GET request
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  /**
   * POST request
   */
  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  /**
   * PUT request
   */
  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  /**
   * PATCH request
   */
  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  /**
   * DELETE request
   */
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  /**
   * Get the underlying Axios instance for advanced usage
   */
  getAxiosInstance(): AxiosInstance {
    return this.client;
  }
}

/**
 * Create and export the default API client instance
 * Uses environment configuration matching current FogForecastService pattern
 */
const createApiClient = (): ApiClient => {
  const baseURL = import.meta.env.VITE_API_BASE_URL || '/api';
  
  console.log('Creating API client with base URL:', baseURL);
  
  return new ApiClient({
    baseURL,
    timeout: 10000,
  });
};

// Export singleton instance
export const apiClient = createApiClient();

// Export the class for testing and custom instances
export { ApiClient };
export type { ApiClientConfig };